import { Component, effect, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { DocumentFormBase } from '../document-form.base';
import { Feature } from 'geojson';
import { dateFinValiditeValidator } from '@lib/validators/date-validition-pj-validators';
import { OptionsService } from '@lib/services/options/options.service';
import { dateValiditePjErrors } from '@lib/validators/date-validition-pj-errors-validators';
import {
  OuiAutocompleteOption,
  OuiDatePicker,
  OuiSelect,
  OuiTextfield,
  OuiValidationError,
} from 'omnium-ui/form-field';
import { siretErrors } from '@lib/validators/siret-errors-validators';
import { AdresseAutocompleteComponent } from '@lib/components/adresse-autocomplete/adresse-autocomplete.component';
import { OuiInfosBulle } from 'omnium-ui/infos-bulle';
import { OuiLoading } from 'omnium-ui/loading';
import { DocumentsConsultantService } from '@lib/services/documents/documents-consultant.service';
import { NumbersRegex } from '@lib/components/controle-documents/models/regex-numbers.model';
import moment from 'moment';

@Component({
  selector: 'app-document-form-rsac',
  standalone: true,
  imports: [
    OuiDatePicker,
    OuiTextfield,
    OuiSelect,
    OuiInfosBulle,
    OuiLoading,
    AdresseAutocompleteComponent,
  ],
  templateUrl: './document-form-rsac.component.html',
  styleUrl: './document-form-rsac.component.scss',
})
export class DocumentFormRsacComponent extends DocumentFormBase<RsacForm> {
  private readonly optionsService = inject(OptionsService);
  private readonly documentConsultantService = inject(DocumentsConsultantService);

  readonly isLoading = signal<boolean>(true);
  readonly countriesOptions = signal<OuiAutocompleteOption<string>[]>([]);
  readonly formeJuridiqueOptions = this.optionsService.formeJuridiqueOptions;

  readonly dateValidationErrors: OuiValidationError[] = dateValiditePjErrors();
  readonly siretErrors: OuiValidationError[] = siretErrors();
  readonly numbersRegex = NumbersRegex;

  constructor() {
    super();
    this.optionsService
      .countriesOptions()
      .then(value => this.countriesOptions.set(value));

    effect(() => {
      if (this.codeConsultant()) {
        this.setUpKnownData();
      } else {
        this.isLoading.set(false);
      }
    });
  }

  override createForm(fb: NonNullableFormBuilder) {
    return createRsacForm(fb);
  }

  onAdressSelect(feature: Feature) {
    const p = feature.properties;

    if (!p) return;

    this.form.patchValue({
      voie: (p['name'] as string) ?? '',
      codePostal: (p['postcode'] as string) ?? '',
      ville: (p['city'] as string) ?? '',
    });
  }

  setDateFinDeValidite(newDate: moment.Moment | string | null) {
    if (newDate != null && typeof newDate != 'string') {
      const dateFinRsac = moment(newDate).add(6, 'months').toDate();
      const newDateFin = newDate ? dateFinRsac : undefined;

      this.form.get('dateFinValidite')?.setValue(newDateFin);
    }
  }

  async setUpKnownData() {
    const docInfo = await this.documentConsultantService.GetConsultantInfoForNewDoc(
      this.codeConsultant()!,
      true
    );

    this.form.patchValue({
      voie: docInfo.voie,
      codePostal: docInfo.codePostal ?? '',
      ville: docInfo.ville ?? '',
      pays: docInfo.pays ?? '',
      numeroSiret: docInfo.numeroSiret ?? '',
      nomCommercial: docInfo.nomCommercial ?? '',
    });

    this.isLoading.set(false);
  }
}

export function createRsacForm(fb: NonNullableFormBuilder) {
  return fb.group({
    voie: fb.control<string | undefined>(undefined),
    codePostal: fb.control<string | undefined>(undefined),
    ville: fb.control<string | undefined>(undefined),
    pays: fb.control<string>('FRA'),
    nomCommercial: fb.control<string | undefined>(undefined),
    numeroSiret: fb.control<string | undefined>(undefined, [
      Validators.required,
      Validators.minLength(14),
      Validators.maxLength(14),
    ]),
    dateDebutValidite: fb.control<Date | undefined>(undefined, [
      Validators.required,
      dateFinValiditeValidator('debut'),
    ]),
    dateFinValidite: fb.control<Date | undefined>(undefined, [
      Validators.required,
      dateFinValiditeValidator('fin'),
    ]),
  });
}

export type RsacForm = ReturnType<typeof createRsacForm>;
export type RsacFormValue = RsacForm['value'];
